import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import {
  CompanyTenantSummary,
  ResolveCodeKind,
  ResolveCodeResult,
  ResolvedCompany,
  ResolvedTenant,
  TenantInfo,
  UrlResolution,
} from '../models/tenant.model';
import { deleteCookie, readCookie, writeCookie } from '../utils/cookie.util';
import { extractSubdomain as extractSubdomainFromHost } from '../utils/hostname.util';

interface AbpResponse<T> {
  result: T;
  success: boolean;
}

interface PersistedTenantContext {
  displayName?: string | null;
  companyId?: number | null;
  companyName?: string | null;
  companyTenantCount?: number | null;
  companyCode?: string | null;
}

const LOCALHOST_HOSTS = ['localhost', '127.0.0.1'];
const TENANT_ID_COOKIE = 'Abp.TenantId';
const TENANT_NAME_KEY = 'prizm.wms.mobile.tenant.name';
const TENANT_CONTEXT_KEY = 'prizm.wms.mobile.tenant.context';
const HOST_TENANT_NAME = 'Default';

const RESOLVE_KIND_TENANT: ResolveCodeKind = 1;
const RESOLVE_KIND_COMPANY: ResolveCodeKind = 2;

@Injectable({ providedIn: 'root' })
export class TenantService {
  private readonly http = inject(HttpClient);

  private readonly _tenant = signal<TenantInfo | null>(this.readPersisted());
  private readonly _pendingCompany = signal<ResolvedCompany | null>(null);
  private readonly _isLocalhost = signal(false);
  private readonly _logoId = signal<string | null>(null);
  private readonly _urlTenantCode = signal<string | null>(null);

  readonly tenant = this._tenant.asReadonly();
  readonly tenantId = () => this._tenant()?.id ?? null;

  /** Multi-tenant company resolved from the URL, pending a warehouse pick on `/tenant`. */
  readonly pendingCompany = this._pendingCompany.asReadonly();

  /** Tenant code taken from the URL subdomain (null on localhost / non-tenant hosts). */
  readonly urlTenantCode = this._urlTenantCode.asReadonly();

  readonly logoUrl = computed(() => {
    const logoId = this._logoId();
    if (!logoId) return null;
    const tenantId = this._tenant()?.id;
    const params = new URLSearchParams({ id: logoId });
    if (tenantId != null) params.set('tenantId', String(tenantId));
    return `${environment.apiUrl}/TenantCustomization/GetTenantLogo?${params.toString()}`;
  });

  constructor() {
    const hostname = globalThis.location?.hostname ?? '';
    this._isLocalhost.set(LOCALHOST_HOSTS.includes(hostname));
    this._urlTenantCode.set(this.extractSubdomain(hostname));
  }

  isLocalhost(): boolean {
    return this._isLocalhost();
  }

  hasPersistedTenant(): boolean {
    return readCookie(TENANT_ID_COOKIE) !== null;
  }

  extractSubdomain(hostname: string): string | null {
    return extractSubdomainFromHost(hostname, environment.appBaseDomain);
  }

  /** Dev-only escape hatch: never applies outside localhost, and only when configured. */
  private devTenantCodeFallback(hostname: string): string | null {
    if (!LOCALHOST_HOSTS.includes(hostname)) return null;
    const code = environment.devTenantCode?.trim();
    return code ? code : null;
  }

  /**
   * Resolves the tenant/company context for the current URL. The URL is the single source of
   * truth: a persisted cache hit is only trusted when its tenancy name matches the subdomain
   * extracted from `hostname`; a bare host (no subdomain) never inherits a cached tenant.
   *
   * On localhost only, `environment.devTenantCode` (when set) stands in for the missing
   * subdomain so local development doesn't always land on /url-error.
   */
  async resolveFromUrl(hostname: string): Promise<UrlResolution> {
    const subdomain = this.extractSubdomain(hostname) ?? this.devTenantCodeFallback(hostname);
    if (!subdomain) {
      // No subdomain at all (and no dev bypass either) — never reaches the backend, so this
      // is unambiguously a URL-shape problem, not an unknown code (see UrlResolutionNoneReason).
      return { type: 'none', reason: 'missing-subdomain' };
    }

    const cached = this._tenant();
    if (cached && cached.name.toLowerCase() === subdomain.toLowerCase()) {
      this._pendingCompany.set(null);
      return { type: 'tenant', tenant: cached };
    }

    const result = await this.fetchResolveCode(subdomain);
    return result ? this.applyResolveResult(result) : { type: 'none', reason: 'unresolved-code' };
  }

  /**
   * Re-resolves a company code directly (independent of the current URL) to repopulate
   * `pendingCompany`. Used by `/tenant` when the user lands there without a pending company
   * already in memory but the current tenant's own `companyCode` is known (see
   * `TenantInfo.companyCode`) — e.g. clicking Change again after already picking a tenant
   * that came from a company-code entry. Does nothing to help when `companyCode` isn't known
   * (a direct tenant-subdomain entry never receives one from the backend); the caller is
   * expected to fall back to `/launch` in that case.
   */
  async resolveCompanyByCode(code: string): Promise<UrlResolution> {
    const result = await this.fetchResolveCode(code);
    // A code is always given here (never called with an empty string — see call site), so a
    // miss is always 'unresolved-code', never 'missing-subdomain'.
    return result ? this.applyResolveResult(result) : { type: 'none', reason: 'unresolved-code' };
  }

  /** Persists the warehouse picked from `pendingCompany()` and clears the pending state. */
  chooseTenant(tenant: CompanyTenantSummary): TenantInfo {
    const company = this._pendingCompany();
    const info: TenantInfo = {
      id: tenant.tenantId,
      name: tenant.tenancyName,
      displayName: tenant.name,
      companyId: company?.companyId ?? null,
      companyName: company?.name ?? null,
      companyTenantCount: company?.tenants.length ?? null,
      companyCode: company?.code ?? null,
    };
    this._tenant.set(info);
    this._pendingCompany.set(null);
    this.persist(info);
    return info;
  }

  setHostTenant(): void {
    const info: TenantInfo = { id: null, name: HOST_TENANT_NAME };
    this._tenant.set(info);
    this.persist(info);
  }

  setLogoId(logoId: string | null): void {
    this._logoId.set(logoId);
  }

  clearTenant(): void {
    this._tenant.set(null);
    this._pendingCompany.set(null);
    this._logoId.set(null);
    deleteCookie(TENANT_ID_COOKIE);
    globalThis.localStorage?.removeItem(TENANT_NAME_KEY);
    globalThis.localStorage?.removeItem(TENANT_CONTEXT_KEY);
  }

  private async fetchResolveCode(code: string): Promise<ResolveCodeResult | null> {
    try {
      return await lastValueFrom(
        this.http
          .post<
            AbpResponse<ResolveCodeResult>
          >(`${environment.apiUrl}/api/services/app/Account/ResolveCode`, { code })
          .pipe(map((r) => r.result)),
      );
    } catch {
      return null;
    }
  }

  private applyResolveResult(result: ResolveCodeResult): UrlResolution {
    if (result.kind === RESOLVE_KIND_TENANT && result.tenant) {
      return { type: 'tenant', tenant: this.storeResolvedTenant(result.tenant) };
    }

    if (result.kind === RESOLVE_KIND_COMPANY && result.company) {
      const { company } = result;

      if (company.tenants.length === 1) {
        const [only] = company.tenants;
        const tenant = this.storeResolvedTenant({
          tenantId: only.tenantId,
          tenancyName: only.tenancyName,
          name: only.name,
          companyId: company.companyId,
          companyName: company.name,
          companyTenantCount: 1,
          companyCode: company.code,
        });
        return { type: 'tenant', tenant };
      }

      if (company.tenants.length > 1) {
        this._pendingCompany.set(company);
        return { type: 'company', company };
      }
    }

    // kind === 0 (none), or an unexpected/empty payload shape. A code reached the
    // backend but didn't resolve to anything (unknown or inactive tenant/company) — distinct
    // from 'missing-subdomain', which never contacts the backend at all.
    return { type: 'none', reason: 'unresolved-code' };
  }

  private storeResolvedTenant(resolved: ResolvedTenant): TenantInfo {
    const info: TenantInfo = {
      id: resolved.tenantId,
      name: resolved.tenancyName,
      displayName: resolved.name,
      companyId: resolved.companyId ?? null,
      companyName: resolved.companyName ?? null,
      companyTenantCount: resolved.companyTenantCount ?? null,
      companyCode: resolved.companyCode ?? null,
    };
    this._tenant.set(info);
    this._pendingCompany.set(null);
    this.persist(info);
    return info;
  }

  private readPersisted(): TenantInfo | null {
    const cookieValue = readCookie(TENANT_ID_COOKIE);
    const cachedName = globalThis.localStorage?.getItem(TENANT_NAME_KEY) ?? null;

    if (cookieValue === null && !cachedName) return null;

    const id = cookieValue === null || cookieValue === '' ? null : Number(cookieValue);
    if (id !== null && Number.isNaN(id)) return null;

    const context = this.readPersistedContext();

    return {
      id,
      name: cachedName ?? (id === null ? HOST_TENANT_NAME : ''),
      displayName: context.displayName ?? undefined,
      companyId: context.companyId ?? null,
      companyName: context.companyName ?? null,
      companyTenantCount: context.companyTenantCount ?? null,
      companyCode: context.companyCode ?? null,
    };
  }

  private readPersistedContext(): PersistedTenantContext {
    const raw = globalThis.localStorage?.getItem(TENANT_CONTEXT_KEY);
    if (!raw) return {};

    try {
      return JSON.parse(raw) as PersistedTenantContext;
    } catch {
      return {};
    }
  }

  private persist(info: TenantInfo): void {
    if (info.id === null) {
      deleteCookie(TENANT_ID_COOKIE);
    } else {
      writeCookie(TENANT_ID_COOKIE, String(info.id));
    }
    if (info.name) {
      globalThis.localStorage?.setItem(TENANT_NAME_KEY, info.name);
    } else {
      globalThis.localStorage?.removeItem(TENANT_NAME_KEY);
    }

    const context: PersistedTenantContext = {
      displayName: info.displayName ?? null,
      companyId: info.companyId ?? null,
      companyName: info.companyName ?? null,
      companyTenantCount: info.companyTenantCount ?? null,
      companyCode: info.companyCode ?? null,
    };
    globalThis.localStorage?.setItem(TENANT_CONTEXT_KEY, JSON.stringify(context));
  }
}
